import React from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router';
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {formatVersionNumber} from './useReportDocumentSubscription';
import {formatTimestamp} from '../../../utils/formatTimestamp';
import RestoreVersionButton from './RestoreVersionButton';

/**
 * Version selector sidebar component.
 * Displays a list of available versions and allows switching between them.
 *
 * @param {Object} props
 * @param {Array<Object>} props.versions - Version docs from Firestore, ordered by version desc
 * @param {string} props.latestVersion - Latest version (padded string). Used to mark the latest.
 * @param {boolean} props.loading - Whether the versions are still loading
 */
const VersionSelector = ({reportId: reportIdProp, versions = [], latestVersion, loading}) => {
    const navigate = useNavigate();
    const {reportId: reportIdParam} = useParams();
    const reportId = reportIdProp ?? reportIdParam;
    const [searchParams, setSearchParams] = useSearchParams();
    const currentVersion = searchParams.get('version');
    const compareTo = searchParams.get('compareTo');

    const handleVersionClick = (version) => {
        if (compareTo) {
            const next = new URLSearchParams(searchParams);
            next.set('version', version);
            if (compareTo === version) {
                next.delete('compareTo');
            }
            navigate(`/products/${reportId}/preview?${next.toString()}`);
            return;
        }
        navigate(`/products/${reportId}/preview?version=${version}`);
    };

    const handleCompareClick = (event, version) => {
        event.stopPropagation();
        const next = new URLSearchParams(searchParams);
        if (compareTo === version) {
            // Toggle off: comparing a version against itself is a no-op.
            next.delete('compareTo');
        } else {
            next.set('version', currentVersion || latestVersion || version);
            next.set('compareTo', version);
        }
        setSearchParams(next);
    };

    if (loading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    width: 220,
                    minWidth: 220,
                    height: '100%',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CircularProgress size={24}/>
            </Paper>
        );
    }

    if (!versions || versions.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    width: 220,
                    minWidth: 220,
                    height: '100%',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    p: 2
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    No versions available
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                width: 220,
                minWidth: 220,
                height: '100%',
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto'
            }}
        >
            <Box sx={{p: 2, pb: 1}}>
                <Typography variant="subtitle2" fontWeight={700}>
                    Versions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {versions.length} total
                </Typography>
            </Box>

            <Divider/>

            <List
                dense
                sx={{
                    flex: 1,
                    py: 0
                }}
            >
                {versions.map((v) => {
                    const versionStr = formatVersionNumber(v?.version) ?? String(v?.version ?? v?.id ?? '');
                    const isSelected = !!versionStr && versionStr === currentVersion;
                    const isLatest = !!versionStr && versionStr === latestVersion;
                    const isCompareTarget = !!versionStr && versionStr === compareTo;

                    const createdAtLabel = v?.createdAt ? formatTimestamp(v.createdAt, '') : '';

                    // Format authors list (AI Assistant last)
                    const authors = v?.versionAuthors || v?.report?.meta?.authors || [];
                    const humanAuthors = authors.filter(a => a.uid !== 'ai' && !a.isAI);
                    const aiAuthors = authors.filter(a => a.uid === 'ai' || a.isAI);
                    const sortedAuthors = [...humanAuthors, ...aiAuthors];
                    const authorsLabel = sortedAuthors
                        .map(a => a.displayName || (a.uid === 'ai' || a.isAI ? 'AI Assistant' : 'Unknown'))
                        .join(', ');

                    return (
                        <ListItem key={v?.id ?? versionStr} disablePadding>
                            <ListItemButton
                                selected={isSelected}
                                onClick={() => handleVersionClick(versionStr)}
                                sx={{
                                    py: 1,
                                    alignItems: 'stretch',
                                    '&.Mui-selected': {
                                        backgroundColor: 'action.selected',
                                        '&:hover': {
                                            backgroundColor: 'action.selected'
                                        }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            mb: 0.5
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 1,
                                                minWidth: 0
                                            }}>
                                                <Typography
                                                    variant="body2"
                                                    fontFamily="monospace"
                                                    fontWeight={isSelected ? 700 : 500}
                                                    sx={{whiteSpace: 'nowrap'}}
                                                >
                                                    v{versionStr}
                                                </Typography>
                                                {isLatest && (
                                                    <Chip
                                                        size="small"
                                                        label="LATEST"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{
                                                            height: 18,
                                                            '& .MuiChip-label': {
                                                                px: 0.75,
                                                                fontSize: '0.65rem',
                                                                fontWeight: 700
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {isCompareTarget && (
                                                    <Chip
                                                        size="small"
                                                        label="COMPARING"
                                                        color="secondary"
                                                        variant="filled"
                                                        sx={{
                                                            height: 18,
                                                            '& .MuiChip-label': {
                                                                px: 0.75,
                                                                fontSize: '0.65rem',
                                                                fontWeight: 700
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                {v?.status && (
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{ml: 1, whiteSpace: 'nowrap'}}>
                                                        {v.status}
                                                    </Typography>
                                                )}
                                                <Tooltip title={isCompareTarget ? 'Stop comparing' : 'Compare against selected version'}>
                                                    <IconButton
                                                        size="small"
                                                        color={isCompareTarget ? 'secondary' : 'default'}
                                                        onClick={(e) => handleCompareClick(e, versionStr)}
                                                        aria-label="Compare version"
                                                    >
                                                        <CompareArrowsIcon fontSize="inherit"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            {authorsLabel && (
                                                <Typography variant="caption" color="text.secondary"
                                                            sx={{display: 'block'}}>
                                                    {authorsLabel}
                                                </Typography>
                                            )}
                                            {createdAtLabel && (
                                                <Typography variant="caption" color="text.secondary"
                                                            sx={{display: 'block'}}>
                                                    {createdAtLabel}
                                                </Typography>
                                            )}
                                            {isSelected && (
                                                <RestoreVersionButton
                                                    reportId={reportId}
                                                    version={versionStr}
                                                    latestVersion={latestVersion}
                                                    disabled={loading}
                                                />
                                            )}
                                        </Box>
                                    }
                                    primaryTypographyProps={{component: 'div'}}
                                    secondaryTypographyProps={{component: 'div'}}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Paper>
    );
};

export default VersionSelector;
